import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, ROLE } from '../lib/auth';

/**
 * The designer's status on a design order — picked from the list or typed as
 * a new one — and how the CRM sees it.  API: GET /api/flow/design-statuses,
 * POST /api/flow/po/:id/design-status.
 */

function fmt(s: string): string {
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
}

const ADD = '__add__';
const CLEAR = '__clear__';

/** Designer: a dropdown of statuses, "+ Add new status…" and "Clear". */
export function DesignStatusSelect({ poId, status }: { poId: string; status: string | null | undefined }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  // Only Super Admin adds a status to the list; everyone else picks from it.
  const canAdd = user?.rollId === ROLE.SuperAdmin;
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const [wait, setWait] = useState(false);
  const list = useQuery({
    queryKey: ['design-statuses'],
    queryFn: () => api<{ statuses: { name: string; clientWait: boolean }[] }>('/api/flow/design-statuses'),
    staleTime: 5 * 60_000,
  });
  const save = useMutation({
    mutationFn: (v: { status: string | null; clientWait?: boolean }) => api(`/api/flow/po/${encodeURIComponent(poId)}/design-status`, {
      method: 'POST', body: JSON.stringify(v),
    }),
    onSuccess: async () => {
      setAdding(false); setText(''); setWait(false);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['flow-queue'] }),
        qc.invalidateQueries({ queryKey: ['designer-dashboard-load'] }),
        qc.invalidateQueries({ queryKey: ['design-statuses'] }),
        qc.invalidateQueries({ queryKey: ['sla-instance'] }),
      ]);
    },
    onError: (e: Error) => alert(e.message === 'design_status_not_available'
      ? 'This needs the latest database update — ask the admin to run the deploy.'
      : e.message === 'status_not_in_list' ? 'Only Super Admin can add a new status — pick one from the list.'
      : `Could not save the status: ${e.message}`),
  });

  const options = list.data?.statuses ?? [];
  const names = options.map((o) => o.name);
  const current = status ?? '';
  const label = (o: { name: string; clientWait: boolean }) => (o.clientWait ? `⏸ ${o.name}` : o.name);
  if (adding && canAdd) {
    return (
      <form className="flex flex-col items-end gap-1" onSubmit={(e) => { e.preventDefault(); if (text.trim()) save.mutate({ status: text.trim(), clientWait: wait }); }}>
        <div className="flex items-center gap-1">
          <input autoFocus className="input w-40 py-0.5 text-xs" maxLength={200} placeholder="New status…"
                 value={text} onChange={(e) => setText(e.target.value)} />
          <button className="btn-primary px-2 py-0.5 text-xs" disabled={!text.trim() || save.isPending}>Save</button>
          <button type="button" className="text-xs text-slate-500 hover:underline" onClick={() => setAdding(false)}>Cancel</button>
        </div>
        <label className="flex items-center gap-1 text-[11px] text-slate-600">
          <input type="checkbox" checked={wait} onChange={(e) => setWait(e.target.checked)} />
          Waiting on the client — stops the TAT clock
        </label>
      </form>
    );
  }
  return (
    <select
      value={current}
      disabled={save.isPending}
      title="Design status — the CRM sees it. ⏸ = waiting on the client: the TAT clock stops"
      onChange={(e) => {
        const v = e.target.value;
        if (v === ADD) { setAdding(true); return; }
        save.mutate({ status: v === CLEAR ? null : v });
      }}
      className={`max-w-[13rem] rounded border px-1.5 py-0.5 text-[11px] font-medium ${
        current ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-slate-300 bg-white text-slate-600'}`}
    >
      <option value="">{current ? (options.find((o) => o.name === current)?.clientWait ? `⏸ ${current}` : current) : 'Set status…'}</option>
      {current && !names.includes(current) && <option value={current}>{current}</option>}
      {options.filter((o) => o.name !== current).map((o) => <option key={o.name} value={o.name}>{label(o)}</option>)}
      {canAdd && <option value={ADD}>＋ Add new status…</option>}
      {current && <option value={CLEAR}>✕ Clear status</option>}
    </select>
  );
}

/** What the CRM (and everyone else) sees: the status, who set it and when. */
export function DesignStatusBadge({ status, by, on, compact = false, clientWait = false }: {
  status: string | null | undefined; by?: string | null; on?: string | null; compact?: boolean;
  /** The status waits on the client: the design TAT clock is stopped. */
  clientWait?: boolean | number | null;
}) {
  if (!status) return null;
  return (
    <span className="inline-block max-w-[16rem] rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800 whitespace-normal"
          title={[by && `set by ${by}`, on && fmt(on)].filter(Boolean).join(', ')}>
      {compact ? null : <span className="font-normal text-amber-700">Design: </span>}{clientWait ? '⏸ ' : ''}{status}
      {clientWait ? <span className="block text-[10px] font-normal text-amber-700">waiting on client · TAT clock stopped</span> : null}
      {!compact && (by || on) && (
        <span className="block text-[10px] font-normal text-amber-700">{by ?? ''}{by && on ? ', ' : ''}{on ? fmt(on) : ''}</span>
      )}
    </span>
  );
}
